import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { getOnlineStatusSubscription } from '@ombori/grid-signals-react';
import useGridMobileInstallationResumedListener from './grid-mobile/use-grid-mobile-installation-resumed-listener';

const OnlineReactContext = React.createContext({
  isOnline: true,
});

interface OnlineContextProviderProps {
  children?: ReactNode;
}

export const OFFLINE_AFTER_FAILED_CHECKS = 2;

const globalOnlineStatusSubscription = getOnlineStatusSubscription();

export const OnlineContextProvider = ({ children }: OnlineContextProviderProps) => {
  const [onlineContextValue, setOnlineContextValue] = useState({ isOnline: true });
  const { lastGridMobileResumedTimestamp } = useGridMobileInstallationResumedListener();
  const consecutiveFailures = useRef(0);

  useEffect(() => {
    globalOnlineStatusSubscription.start();

    return () => {
      globalOnlineStatusSubscription.stop();
    };
  }, [lastGridMobileResumedTimestamp]);

  useEffect(() => {
    const unsubscribe = globalOnlineStatusSubscription.subscribe((isOnline) => {
      // One failed check is often a blip, and going offline tears down the live
      // connection and refetches everything. Only report offline after two in a row.
      consecutiveFailures.current = isOnline ? 0 : consecutiveFailures.current + 1;

      if (isOnline || consecutiveFailures.current >= OFFLINE_AFTER_FAILED_CHECKS) {
        setOnlineContextValue((current) => (current.isOnline === isOnline ? current : { isOnline }));
      }
    });

    return () => {
      unsubscribe();
    };
  }, [setOnlineContextValue]);

  return <OnlineReactContext.Provider value={onlineContextValue}>{children}</OnlineReactContext.Provider>;
};

export const useOnlineContext = () => {
  return useContext(OnlineReactContext);
};
